import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import type { ComponentType } from "@repo/editor/types";
import { observer } from "mobx-react-lite";
import React, { forwardRef } from "react";
import * as t from "@rekajs/types";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

export const SalesOfficeDefinition: ComponentSettingsProps = {
  title: {
    type: "string",
    label: "عنوان",
  },
  address: {
    type: "string",
    label: "آدرس",
  },
  phone: {
    type: "string",
    label: "شماره همراه",
  },
  latitude: {
    type: "number",
    label: "latitude",
  },
  longitude: {
    type: "number",
    label: "longitude",
  },
  sideTitle: {
    type: "string",
    label: "عنوان کناری",
  },
};

const LeafletMap = dynamic(() => import("../components/common/map-view"), {
  ssr: false,
});

interface SalesOfficeProps {
  title: string;
  address: string;
  phone: string;
  latitude?: number;
  longitude?: number;
  sideTitle: string;
}

export const SalesOfficeComponent = forwardRef<
  HTMLDivElement,
  SalesOfficeProps
>(({ title, address, phone, latitude, longitude, sideTitle }, ref) => {
  const translations = useTranslations();
  return (
    <div className="relative">
      <div
        className="mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 flex-col md:flex-row gap-6"
        ref={ref}
      >
        <div className="col-span-1 self-end lg:col-span-3 container">
          <h2 className="text-primary font-helvetica lg:text-3xl text-xl uppercase mb-4 rtl:font-doran">
            {title}
          </h2>
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-lg lg:text-xl font-helvetica rtl:font-doran">
                {translations("SalesOffice.address")}
              </p>
              <p className="text-base lg:text-lg rtl:font-doran">{address}</p>
            </div>
            <div>
              <p className="text-lg lg:text-xl font-helvetica rtl:font-doran">
                {translations("SalesOffice.phone")}
              </p>
              <p className="text-base lg:text-lg rtl:font-doran">{phone}</p>
            </div>
          </div>
        </div>
        <div className="col-span-1 lg:col-span-2">
          <div className="relative">
            <div className="absolute top-0 right-0 w-0 h-0 border-l-[100px] border-l-transparent border-t-[100px] border-t-white" />
            {latitude && longitude ? (
              <LeafletMap mapParams={{ center: [latitude, longitude] }} />
            ) : null}
          </div>
        </div>
      </div>
      {sideTitle ? (
        <div className="absolute ltr:left-10 rtl:right-10 top-3/4 lg:flex hidden">
          <div className="size-full relative flex items-center justify-center">
            <div className="absolute rtl:rotate-90 ltr:-rotate-90 font-thin lg:text-3xl text-xl px-2 text-gray-300 before:content-[''] before:inline-block before:bg-gray-300 before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-gray-300 after:h-px after:lg:w-14 after:w-10 flex items-center">
              <span className="mx-2 uppercase rtl:-mt-3 whitespace-nowrap rtl:font-doran rtl:font-medium">
                {sideTitle}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

SalesOfficeComponent.displayName = "SalesOffice";

export const SalesOffice = observer(
  SalesOfficeComponent
) as unknown as ComponentType<SalesOfficeProps>;

export const salesOfficeSettings: ComponentSettings = {
  name: "ESalesOffice",
  props: SalesOfficeDefinition,
  label: "نقشه",
  widgets: {
    label: "نقشه",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "ESalesOffice", external: true }),
      }),
  },
};

SalesOffice.componentSettings = salesOfficeSettings;
